import { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { ReactComponent as ArrowDownIcon } from '../../assets/icons/arrow-down-icon.svg';
import { ReactComponent as CheckIcon } from '../../assets/icons/check-icon.svg';
import { useSidebarOpen } from '../../contexts/SidebarContext';
import { useMediaQuery } from "../../hooks/useMediaQuery";

// ─── MultiSelectDropdown ──────────────────────────────────────────────────────
// Props:
//   label       {string}    Prefix label e.g. "Склад"
//   placeholder {string}    Text when nothing selected e.g. "Все"
//   options     {Array}     [{ value: string, label: string, icon?: string }]
//   onChange    {Function}  Called with array of selected values on apply/close
const MultiSelectDropdown = ({ label, options = [], placeholder = "Все", onChange, selectedOptions, applySidebarAlignment = false, filterSelectStyle = null }) => {
  const showFullLabels = useMediaQuery('(min-width: 1580px)');

  // Normalize: accept both ["Almaty"] and [{ value, label, icon }]
  const normalizedOptions = options ? options?.map((o) =>
    typeof o === "string" ? { value: o, label: o } : o
  ) : [];

  const normalizeValue = (v) => (typeof v === "string" ? v : v.value);

  const sidebarOpen = useSidebarOpen();
  const [open, setOpen] = useState(false);
  const [committed, setCommitted] = useState(selectedOptions.map(normalizeValue));
  const [draft, setDraft] = useState(selectedOptions.map(normalizeValue));
  const ref = useRef(null);
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ placement: "bottom", top: 0, bottom: "auto", left: 0, width: 0, maxHeight: 0 });

  // Pin the portal menu to the trigger. Using fixed coordinates derived from
  // the trigger's rect lets the menu render in a body-level portal, so no
  // ancestor with `overflow: auto/hidden` can clip or scroll it away.
  //
  // The menu's max height is derived from the space actually available between
  // the trigger and the viewport edge, and it flips above the trigger when
  // there's more room there — so the footer (reset/apply) is never clipped.
  const updateMenuPosition = useCallback(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const gap = 6;         // distance between trigger and menu
    const margin = 8;      // keep this much off the viewport edges
    const preferred = 360; // never grow taller than this

    const width = rect.width;
    const left = Math.max(margin, Math.min(rect.left, window.innerWidth - width - margin));

    const spaceBelow = window.innerHeight - rect.bottom - gap - margin;
    const spaceAbove = rect.top - gap - margin;
    const openUp = spaceBelow < preferred && spaceAbove > spaceBelow;
    const space = openUp ? spaceAbove : spaceBelow;
    const maxHeight = Math.min(preferred, Math.max(120, space));

    if (openUp) {
      setMenuPos({ placement: "top", top: "auto", bottom: window.innerHeight - rect.top + gap, left, width, maxHeight });
    } else {
      setMenuPos({ placement: "bottom", top: rect.bottom + gap, bottom: "auto", left, width, maxHeight });
    }
  }, []);

  // Recompute position while open: on mount-of-menu, and on any scroll
  // (capture phase catches scrolling inside ancestor containers) or resize.
  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
    const handle = () => updateMenuPosition();
    window.addEventListener("scroll", handle, true);
    window.addEventListener("resize", handle);
    return () => {
      window.removeEventListener("scroll", handle, true);
      window.removeEventListener("resize", handle);
    };
  }, [open, updateMenuPosition]);

  // Sync when selectedOptions changes externally (e.g. updated from another component)
  useEffect(() => {
    const normalized = (selectedOptions || []).map(normalizeValue);
    setCommitted(normalized);
    if (!open) setDraft(normalized);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedOptions)]);

  // Close on outside click — apply draft. The menu lives in a body portal, so
  // clicks inside it must also count as "inside" (check both refs).
  useEffect(() => {
    const handler = (e) => {
      if (!open) return;
      if (ref.current && ref.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      applyDraft(draft);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draft]);

  const applyDraft = (values) => {
    setCommitted(values);
    setOpen(false);
    onChange?.(values);
  };

  const handleOpen = () => {
    setDraft(committed);
    setOpen(true);
  };

  const toggleOption = (value) => {
    setDraft((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const toggleAll = () => {
    setDraft(draft.length === normalizedOptions.length ? [] : normalizedOptions.map((o) => o.value));
  };

  const handleReset = () => {
    setDraft([]);
    applyDraft([]);
  };
  
  const allChecked = draft.length === normalizedOptions.length;
  const someChecked = draft.length > 0 && !allChecked;
  const hasCommitted = committed.length > 0;

  const displayLabel =
    committed.length === 0 ? ""
      : allChecked ? "Все" : normalizedOptions.filter((o) => committed.includes(o.value)).map((o) => o.label).join(", ");

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block", ...filterSelectStyle }}>
      <button
        onClick={() => open ? applyDraft(draft) : handleOpen()}
        className="filter-select"
        style={{ width: "100%" }}
        type="button"
      >
        {label ? <span className="filter-select-label">{label}:</span> : ""}
        
        <div style={{display: "flex", gap: 8, alignItems: "center", overflow: "hidden"}}>
          <span className="filter-select-option">
            {
              applySidebarAlignment ? 
                (!sidebarOpen || showFullLabels) && displayLabel
              : displayLabel
            }
          </span>
          {hasCommitted && (
            <span style={{ background: "#7CC805", color: "#fff", borderRadius: 20, fontSize: 11, fontWeight: 700, padding: "1px 7px", lineHeight: "16px", flexShrink: 0 }}>
              {committed.length}
            </span>
          )}
        </div>
        <ArrowDownIcon size={24} color='#7CC805'/>
      </button>

      {open && createPortal(
        <div ref={menuRef} className="dropdown-portal" style={{
          position: "fixed", top: menuPos.top, bottom: menuPos.bottom, left: menuPos.left, minWidth: menuPos.width,
          maxHeight: menuPos.maxHeight, display: "flex", flexDirection: "column",
          background: "#ffffff", border: "1.5px solid #d1d5db", borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,0.10)", zIndex: 3000, overflow: "hidden",
          animation: "dropIn 0.15s ease",
        }}>
            <label
              style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", cursor: "pointer", borderBottom: "1px solid #f0f0f0", background: allChecked ? "rgba(124,200,5,0.06)" : "transparent", transition: "background 0.12s", fontSize: 14, fontWeight: 600, color: "#6D6D6D" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(124,200,5,0.06)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = allChecked ? "rgba(124,200,5,0.06)" : "transparent")}
            >
              <Checkbox checked={allChecked} indeterminate={someChecked} onChange={toggleAll} />
              Все
            </label>
          <div style={{ flex: "1 1 auto", minHeight: 0, overflow: "auto" }}>
            {normalizedOptions.map((opt) => {
              const isChecked = draft.includes(opt.value);
              return (
                <label key={opt.value}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", cursor: "pointer", background: isChecked ? "rgba(124,200,5,0.06)" : "transparent", transition: "background 0.12s", fontSize: 14, fontWeight: 400, color: "#1a1a1a" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(124,200,5,0.06)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = isChecked ? "rgba(124,200,5,0.06)" : "transparent")}
                >
                  <Checkbox checked={isChecked} onChange={() => toggleOption(opt.value)} />
                  {opt.icon && <span style={{ fontSize: 16 }}>{opt.icon}</span>}
                  {opt.label}
                </label>
              );
            })}
          </div>

          <div style={{ flexShrink: 0, display: "flex", gap: 8, padding: "10px", borderTop: "1px solid #f0f0f0", background: "#fff" }}>
            <button
              onClick={handleReset}
              type="button"
              style={{ flex: 1, padding: "7px", borderRadius: 8, border: "1.5px solid #e7e7e7", background: "#fff", color: "#555", fontSize: 12.5, fontWeight: 500, cursor: "pointer" }}
            >Сбросить</button>
            <button
              onClick={() => applyDraft(draft)}
              type="button"
              style={{ flex: 1, padding: "7px", borderRadius: 8, border: "none", background: "#7CC805", color: "#fff", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}
            >Применить</button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Checkbox (internal) ──────────────────────────────────────────────────────
function Checkbox({ checked, indeterminate, onChange }) {
  const inputRef = useRef(null);
  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = !!indeterminate;
  }, [indeterminate]);

  return (
    <span style={{ position: "relative", display: "inline-flex", alignItems: "center", flexShrink: 0 }}>
      <input
        ref={inputRef}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        style={{ position: "absolute", opacity: 0, width: 18, height: 18, cursor: "pointer", margin: 0 }}
      />
      <span
        style={{
          width: 18,
          height: 18,
          borderRadius: 5,
          border: checked || indeterminate ? "2px solid #7CC805" : "2px solid #d1d5db",
          background: checked || indeterminate ? "#7CC805" : "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.15s",
          flexShrink: 0,
        }}
      >
        {indeterminate && !checked ? (
          <svg width="10" height="2" viewBox="0 0 10 2" fill="none">
            <path d="M1 1h8" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
          </svg>
        ) : checked ? (
            <CheckIcon color="#fff"/>
        ) : null}
      </span>
    </span>
  );
}

export default MultiSelectDropdown;
